/**
 * @file keys.ts
 * @description Роли цветов оформления сайта магазина, версия 1 (phase19.16,
 * релиз Р1; правила SB8, SB9; проект данных db-design-r1 §1.1).
 *
 * Зачем файл: владелец задаёт любой цвет для каждой роли, а витрина, корзина,
 * чекаут, ЛК и блог красятся только через роли — одна правка перекрашивает
 * весь сайт (SB8). Готовых тем в коде нет (фаза §7.0, SB1): здесь только
 * состав ролей и подписи для кабинета.
 *
 * Ключи ролей ЗАМОРОЖЕНЫ с Р1: на них в Р2 ссылается оформление секций
 * (`{ bg: { kind: "role", role: "panelBg" } }`). Переименование ключа —
 * только новой версией схемы (`SITE_THEME_SCHEMA`) и функцией upgrade.
 * Подписи для людей можно менять свободно — они не хранятся в данных.
 */

/**
 * Версия состава документа оформления (`SiteDraft.themeSchema`,
 * `SiteRevision.schema`, `SiteDesign.schema`). 2 — SB120 этап 2 «оформление
 * сайта числами» (db-design-sb120-stage2 §2, вариант В): ступени v1 стали
 * числами; подъём — `liftThemeV1` (legacy-v1.ts), он же вызывается при
 * чтении документа любой версии.
 */
export const SITE_THEME_SCHEMA = 2;

/** Девять ролей цвета SB8 в порядке показа в кабинете. */
export const SITE_COLOR_ROLES = [
  "pageBg",
  "cardBg",
  "panelBg",
  "text",
  "textMuted",
  "button",
  "accent",
  "darkBg",
  "onDark",
] as const;

export type SiteColorRole = (typeof SITE_COLOR_ROLES)[number];

/** Цвета ролей: только `#rrggbb` / `#rrggbbaa` (как палитра кабинета). */
export type SiteColors = Readonly<Record<SiteColorRole, string>>;

/** Группа в списке «Цвета» на экране «Сайт → Оформление» (uiux-design-r1 §1). */
export type SiteColorGroup = "surface" | "text" | "action" | "dark";

/** Подписи групп — заголовки капсом в списке цветов. */
export const SITE_COLOR_GROUP_LABELS: Readonly<Record<SiteColorGroup, string>> = {
  surface: "Фон и поверхности",
  text: "Текст",
  action: "Действия",
  dark: "Тёмные секции",
};

/** Описание роли для кабинета. */
export interface SiteColorRoleInfo {
  readonly key: SiteColorRole;
  readonly group: SiteColorGroup;
  /** Название роли: «Фон страницы». */
  readonly label: string;
  /** Где цвет виден на сайте — строка под названием и в окне цвета («Где: …»). */
  readonly usedFor: string;
}

/**
 * Реестр ролей. Пояснения «где используется» — по макету uiux-design-r1 §1;
 * они же объясняют владельцу, почему одна правка меняет и ЛК, и чекаут.
 */
export const SITE_COLOR_ROLE_INFO: Readonly<Record<SiteColorRole, SiteColorRoleInfo>> = {
  pageBg: {
    key: "pageBg",
    group: "surface",
    label: "Фон страницы",
    usedFor: "весь сайт под блоками, корзина, оформление заказа, кабинет покупателя",
  },
  cardBg: {
    key: "cardBg",
    group: "surface",
    label: "Карточки",
    usedFor: "карточки товаров и статей, корзина, окна, блоки «в карточке»",
  },
  panelBg: {
    key: "panelBg",
    group: "surface",
    label: "Плашки",
    usedFor: "выделенные блоки, поля ввода, подложки бейджей и подсказок",
  },
  text: {
    key: "text",
    group: "text",
    label: "Текст",
    usedFor: "заголовки и основной текст на светлых фонах",
  },
  textMuted: {
    key: "textMuted",
    group: "text",
    label: "Второй текст",
    usedFor: "подписи, даты, пояснения, старая цена",
  },
  button: {
    key: "button",
    group: "action",
    label: "Кнопки",
    usedFor: "«В корзину», «Оформить заказ», бейджи, счётчик корзины",
  },
  accent: {
    key: "accent",
    group: "action",
    label: "Акцент",
    usedFor: "ссылки, надписи капсом, значки, кольцо фокуса",
  },
  darkBg: {
    key: "darkBg",
    group: "dark",
    label: "Тёмные секции",
    usedFor: "тёмная шапка и подвал, секции с тёмным фоном",
  },
  onDark: {
    key: "onDark",
    group: "dark",
    label: "Текст на тёмном",
    usedFor: "текст и значки в тёмной шапке, подвале и тёмных секциях",
  },
};

/** Формат цвета роли: `#rrggbb` или `#rrggbbaa` (короткий hex, rgb() и имена не принимаются). */
export const SITE_COLOR_PATTERN = /^#[0-9a-fA-F]{6}(?:[0-9a-fA-F]{2})?$/;

/** Сужение строки до ключа роли (для данных из JSON и параметров запроса). */
export function isSiteColorRole(key: string): key is SiteColorRole {
  return (SITE_COLOR_ROLES as readonly string[]).includes(key);
}

/**
 * Имя CSS-переменной роли: `pageBg` → `--site-page-bg`. Одна функция для
 * переменных темы (css.ts) и ссылок на цвет (color-ref.ts) — имена не разойдутся.
 */
export function siteColorVar(role: SiteColorRole): string {
  return `--site-${role.replace(/[A-Z]/g, (ch) => `-${ch.toLowerCase()}`)}`;
}
